import React, { useRef, useState, useEffect } from "react";
import ClearCacheButton from "../components/ClearCacheButton";
import {
  clearExpiredImageCaches,
  DEFAULT_EXPIRATION,
} from "@/utils/getCachedImage";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  ArrowLeftIcon,
  ChevronRightIcon,
  LogOutIcon,
  HelpCircleIcon,
  ShieldIcon,
  InfoIcon,
  EyeOffIcon,
  EyeIcon,
} from "../components/icons/FeatherIcons";
import { usePageTracking } from "../hooks/usePageTracking";
import { LinkedAccountsSection } from "../components/LinkedAccountsSection";
import ConfirmModal from "../components/ConfirmModal";
import { auth } from "../services/firebase";
import {
  startBackgroundLocationUpdates,
  stopBackgroundLocationUpdates,
} from "../services/backgroundLocationService";

// Simple spinner component
const Spinner = () => (
  <svg
    className="animate-spin h-5 w-5 mr-2 text-red-600"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    ></circle>
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
    ></path>
  </svg>
);

const SettingsPage: React.FC = () => {
  usePageTracking("Settings", "SettingsPage");
  const navigate = useNavigate();
  const {
    logout,
    deleteAccount,
    deleteAuthUser,
    loginWithGoogle,
    loginWithApple,
    login,
    currentUser,
    getLinkedProviders,
    authFirebaseUser,
    updateUser,
  } = useAuth();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeletedAck, setShowDeletedAck] = useState(false);
  const [reauthError, setReauthError] = useState("");
  const [reauthPassword, setReauthPassword] = useState("");
  const [reauthLoading, setReauthLoading] = useState(false);
  const [deletingAfterReauth, setDeletingAfterReauth] = useState(false);
  const [showReauthModal, setShowReauthModal] = useState(false);
  const currentUserBeforeReauth = useRef(null as string | null);
  const [showPassword, setShowPassword] = useState(false);
  const [matchSuggestionsEnabled, setMatchSuggestionsEnabled] = useState(
    currentUser?.notificationSettings?.matchSuggestionsEnabled ?? true,
  );
  const [matchSuggestionsMaxPerDay, setMatchSuggestionsMaxPerDay] = useState(
    currentUser?.notificationSettings?.matchSuggestionsMaxPerDay ?? 1,
  );
  const [matchSettingsSaving, setMatchSettingsSaving] = useState(false);
  const [backgroundLocationEnabled, setBackgroundLocationEnabled] = useState(
    currentUser?.notificationSettings?.backgroundLocationEnabled ?? true,
  );
  // Cache expiration options (in ms)
  const cacheOptions = [
    { label: "1 day", value: 1 * 24 * 60 * 60 * 1000 },
    { label: "7 days", value: 7 * 24 * 60 * 60 * 1000 },
    { label: "30 days", value: 30 * 24 * 60 * 60 * 1000 },
  ];

  const [cacheExpiration, setCacheExpiration] = useState(() => {
    const stored = localStorage.getItem("imageCacheExpiration");
    return stored ? Number(stored) : DEFAULT_EXPIRATION;
  });

  useEffect(() => {
    localStorage.setItem("imageCacheExpiration", String(cacheExpiration));
    clearExpiredImageCaches(cacheExpiration);
  }, [cacheExpiration]);

  useEffect(() => {
    if (!currentUser) return;
    setMatchSuggestionsEnabled(
      currentUser.notificationSettings?.matchSuggestionsEnabled ?? true,
    );
    setMatchSuggestionsMaxPerDay(
      currentUser.notificationSettings?.matchSuggestionsMaxPerDay ?? 1,
    );
    setBackgroundLocationEnabled(
      currentUser.notificationSettings?.backgroundLocationEnabled ?? true,
    );
  }, [currentUser]);

  const handleMatchSettingChange = async (
    nextEnabled: boolean,
    nextMaxPerDay: number,
    nextBackgroundEnabled: boolean,
  ) => {
    if (!currentUser) return;
    setMatchSettingsSaving(true);
    try {
      await updateUser({
        notificationSettings: {
          matchSuggestionsEnabled: nextEnabled,
          matchSuggestionsMaxPerDay: nextMaxPerDay,
          backgroundLocationEnabled: nextBackgroundEnabled,
        },
      });
    } catch (error) {
      // Revert on failure
      setMatchSuggestionsEnabled(
        currentUser.notificationSettings?.matchSuggestionsEnabled ?? true,
      );
      setMatchSuggestionsMaxPerDay(
        currentUser.notificationSettings?.matchSuggestionsMaxPerDay ?? 1,
      );
      setBackgroundLocationEnabled(
        currentUser.notificationSettings?.backgroundLocationEnabled ?? true,
      );
    } finally {
      setMatchSettingsSaving(false);
    }
  };

  // Helper: get provider using getLinkedProviders
  const getProvider = () => {
    const providers = getLinkedProviders();
    if (!providers || providers.length === 0) return "password";
    // Prefer google if present, else apple, else password
    if (providers.some((p) => p.providerId === "google.com")) return "google";
    if (providers.some((p) => p.providerId === "apple.com")) return "apple";
    if (providers.some((p) => p.providerId === "password")) return "password";
    // fallback to first provider
    return providers[0].providerId;
  };
  const provider = getProvider();

  // Re-authentication logic
  const handleReauthGoogle = async () => {
    setReauthError("");
    setReauthLoading(true);
    try {
      await loginWithGoogle();
    } catch (error: any) {
      setReauthError(error?.message || "Google re-authentication failed.");
    } finally {
      setReauthLoading(false);
    }
  };

  const handleReauthApple = async () => {
    setReauthError("");
    setReauthLoading(true);
    try {
      await loginWithApple();
    } catch (error: any) {
      setReauthError(error?.message || "Apple re-authentication failed.");
    } finally {
      setReauthLoading(false);
    }
  };

  const handleReauthPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reauthPassword) {
      setReauthError("Enter your password to continue.");
      return;
    }
    setReauthError("");
    setReauthLoading(true);
    try {
      await login(currentUser?.email || "", reauthPassword);
    } catch (error: any) {
      setReauthError(error?.message || "Re-authentication failed.");
    } finally {
      setReauthLoading(false);
    }
  };

  const deleteAccountAfterReauth = async () => {
    setReauthError("");
    setDeletingAfterReauth(true);
    try {
      if (!currentUserBeforeReauth.current) {
        throw new Error(
          "Internal error: No previous user found before re-authentication.",
        );
      }
      let allowedToDeleteUser = currentUserBeforeReauth.current;
      // check if the user after reauth is the same
      if (!authFirebaseUser.current) {
        throw new Error(
          "No authenticated user found after Google re-authentication.",
        );
      }
      if (authFirebaseUser.current.uid !== allowedToDeleteUser) {
        throw new Error(
          "Re-authenticated user does not match the current user. Please choose the correct Google account.",
        );
      }
      await deleteAccount();
      await deleteAuthUser();
      setIsDeleting(false);
      setShowReauthModal(false);
      setShowDeletedAck(true);
    } catch (error: any) {
      setReauthError(error?.message || "Account Deletion failed.");
    } finally {
      setDeletingAfterReauth(false);
      setReauthPassword("");
    }
  };

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    setShowReauthModal(true);
    setShowDeleteConfirm(false);
    authFirebaseUser.current = null;
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const settingsItems = [
    {
      name: "View Onboarding",
      icon: InfoIcon,
      action: () => navigate("/onboarding"),
      status: undefined,
    },
    {
      name: "Help & Support",
      icon: HelpCircleIcon,
      action: () => navigate("/support"),
      status: undefined,
    },
    {
      name: "Privacy & Security",
      icon: ShieldIcon,
      action: () => navigate("/privacy-security"),
      status: undefined,
    },
  ];

  const handleBack = () => {
    navigate("/profile", { replace: true });
  };

  return (
    <>
      {/* Blur the page when acknowledgement modal is open */}
      <div
        className={
          showDeleteConfirm || showDeletedAck || showReauthModal
            ? "blur-3xl pointer-events-none select-none transition-all duration-200"
            : ""
        }
      >
        {isDeleting ? (
          <span className="flex items-center justify-center">
            <Spinner />
            Deleting Account...
          </span>
        ) : (
          <div className="flex flex-col h-full bg-white pb-6">
            <header className="flex-shrink-0 bg-white px-3 pb-3 pt-3 flex items-center shadow-sm border-b border-gray-200 sticky-header">
              <button
                onClick={() => navigate(-1)}
                className="text-gray-500 mr-4 p-2 rounded-full hover:bg-gray-100"
              >
                <ArrowLeftIcon className="w-6 h-6" />
              </button>
              <h1 className="text-xl font-bold text-gray-900">Settings</h1>
            </header>

            <main className="flex-1 overflow-y-auto p-4 page-content-top">
              <div className="max-w-3xl mx-auto space-y-6">
                {/* Linked Accounts Section */}
                <LinkedAccountsSection />

                {/* Settings Items */}
                <div className="space-y-2">
                  {settingsItems.map((item) => (
                    <button
                      key={item.name}
                      onClick={item.action}
                      className="w-full flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg text-left hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center">
                        <item.icon className="w-6 h-6 mr-4 text-gray-500" />
                        <span className="text-gray-800 font-semibold">
                          {item.name}
                        </span>
                      </div>
                      <div className="flex items-center">
                        {item.status && (
                          <span
                            className={`text-sm mr-2 font-semibold ${item.status === "Set" ? "text-green-600" : "text-gray-500"}`}
                          >
                            {item.status}
                          </span>
                        )}
                        <ChevronRightIcon className="w-5 h-5 text-gray-400" />
                      </div>
                    </button>
                  ))}
                </div>

                {/* Cache Management Section */}
                <div className="mt-6">
                  <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-2">
                    App Management
                  </h2>
                  <div className="p-4 bg-white rounded-lg border border-gray-200 flex flex-col gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Profile Image Cache Expiration
                      </label>
                      <select
                        value={cacheExpiration}
                        onChange={(e) =>
                          setCacheExpiration(Number(e.target.value))
                        }
                        className="w-full max-w-xs p-2 border border-gray-300 rounded-lg"
                      >
                        {cacheOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Expired cached images will be cleared automatically.
                      </p>
                    </div>
                    <ClearCacheButton />
                  </div>
                </div>

                {/* Match Suggestions Section */}
                <div className="mt-6">
                  <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-2">
                    Match Suggestions
                  </h2>
                  <div className="p-4 bg-white rounded-lg border border-gray-200 flex flex-col gap-4">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p className="text-sm font-semibold text-gray-800">
                          Suggest nearby activity matches
                        </p>
                        <p className="text-xs text-gray-500">
                          Use your location to suggest small groups you may
                          enjoy.
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          const nextEnabled = !matchSuggestionsEnabled;
                          setMatchSuggestionsEnabled(nextEnabled);
                          handleMatchSettingChange(
                            nextEnabled,
                            matchSuggestionsMaxPerDay,
                            backgroundLocationEnabled,
                          );
                        }}
                        disabled={matchSettingsSaving}
                        className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${matchSuggestionsEnabled ? "bg-primary" : "bg-gray-300"} ${matchSettingsSaving ? "opacity-60 cursor-not-allowed" : ""}`}
                        aria-label="Toggle match suggestions"
                      >
                        <span
                          className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${matchSuggestionsEnabled ? "translate-x-6" : "translate-x-1"}`}
                        />
                      </button>
                    </div>

                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p className="text-sm font-semibold text-gray-800">
                          Max suggestions per day
                        </p>
                        <p className="text-xs text-gray-500">
                          Limit how many match prompts you receive.
                        </p>
                      </div>
                      <select
                        value={matchSuggestionsMaxPerDay}
                        onChange={(e) => {
                          const nextValue = Number(e.target.value);
                          setMatchSuggestionsMaxPerDay(nextValue);
                          handleMatchSettingChange(
                            matchSuggestionsEnabled,
                            nextValue,
                            backgroundLocationEnabled,
                          );
                        }}
                        disabled={
                          matchSettingsSaving || !matchSuggestionsEnabled
                        }
                        className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white"
                      >
                        {[0, 1, 2, 3, 4, 5].map((value) => (
                          <option key={value} value={value}>
                            {value === 0 ? "Off" : value}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p className="text-sm font-semibold text-gray-800">
                          Background location updates
                        </p>
                        <p className="text-xs text-gray-500">
                          Allow low-power updates to keep suggestions timely.
                        </p>
                      </div>
                      <button
                        onClick={async () => {
                          const nextEnabled = !backgroundLocationEnabled;
                          setBackgroundLocationEnabled(nextEnabled);
                          handleMatchSettingChange(
                            matchSuggestionsEnabled,
                            matchSuggestionsMaxPerDay,
                            nextEnabled,
                          );

                          if (currentUser) {
                            if (nextEnabled) {
                              await startBackgroundLocationUpdates(
                                currentUser.id,
                              );
                            } else {
                              await stopBackgroundLocationUpdates();
                            }
                          }
                        }}
                        disabled={matchSettingsSaving}
                        className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${backgroundLocationEnabled ? "bg-primary" : "bg-gray-300"} ${matchSettingsSaving ? "opacity-60 cursor-not-allowed" : ""}`}
                        aria-label="Toggle background location"
                      >
                        <span
                          className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${backgroundLocationEnabled ? "translate-x-6" : "translate-x-1"}`}
                        />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Log Out and Delete Account Buttons */}
                <div className="mt-4">
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center justify-center p-4 bg-white border border-gray-200 rounded-lg text-left text-red-500 hover:bg-red-500/10 transition-colors"
                  >
                    <LogOutIcon className="w-6 h-6 mr-3" />
                    <span className="font-semibold">Log Out</span>
                  </button>
                </div>

                <div className="mt-4">
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className={`w-full flex items-center justify-center p-4 bg-red-50 text-red-600 rounded-lg font-semibold hover:bg-red-100 transition-colors ${isDeleting ? "opacity-50 cursor-not-allowed" : ""}`}
                    disabled={isDeleting}
                  >
                    {isDeleting ? "Deleting Account..." : "Delete Account"}
                  </button>
                </div>
              </div>
            </main>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        title="Delete Account?"
        message="This action is permanent. All your data will be removed from our servers and you will lose access to your account."
        confirmText={isDeleting ? "Deleting..." : "Delete My Account"}
        onConfirm={handleDeleteAccount}
        onCancel={() => {
          setShowDeleteConfirm(false);
          authFirebaseUser.current = auth?.currentUser || null;
        }}
      />

      {/* Acknowledgement Modal after deletion */}
      <ConfirmModal
        isOpen={showDeletedAck}
        title="Account Deleted"
        message="Your account has been deleted. Thank you for using our app."
        confirmText="Ok"
        acknowledgeOnly={true}
        onConfirm={() => {
          setShowDeletedAck(false);
        }}
      >
        <div className="mt-2 text-sm text-gray-500 text-center">
          You will be redirected to the Welcome page.
        </div>
      </ConfirmModal>

      {/* Confirmation and Re-authentication Modal */}
      <ConfirmModal
        isOpen={showReauthModal}
        title="Re-authenticate to Delete Account"
        message="For security, please re-authenticate your account before deleting it."
        confirmText={
          reauthLoading
            ? "Re-authenticating..."
            : deletingAfterReauth
              ? "Deleting..."
              : provider === "password"
                ? "Delete My Account"
                : provider === "google"
                  ? "Continue with Google"
                  : "Continue with Apple"
        }
        onConfirm={async () => {
          currentUserBeforeReauth.current = auth?.currentUser?.uid || null;
          if (provider === "google") {
            await handleReauthGoogle();
          } else if (provider === "apple") {
            await handleReauthApple();
          } else {
            await handleReauthPassword({
              preventDefault: () => {},
            } as React.FormEvent);
          }
          if (reauthError) return;
          await deleteAccountAfterReauth();
        }}
        onCancel={() => {
          setIsDeleting(false);
          setShowReauthModal(false);
          setReauthPassword("");
          setReauthError("");
        }}
      >
        {provider === "password" ? (
          <div className="flex flex-col items-center pb-6">
            <form>
              {/* Visually hidden username/email field for accessibility and autofill */}
              <input
                type="email"
                name="email"
                autoComplete="username"
                value={currentUser?.email || ""}
                readOnly
                tabIndex={-1}
                aria-hidden="true"
                style={{
                  position: "absolute",
                  left: "-9999px",
                  width: "1px",
                  height: "1px",
                  opacity: 0,
                }}
              />
              <div className="relative w-64 mt-4">
                <input
                  type={showPassword ? "text" : "password"}
                  className="p-2 border rounded w-full pr-10"
                  placeholder="Enter your password"
                  value={reauthPassword}
                  onChange={(e) => setReauthPassword(e.target.value)}
                  required
                  autoFocus
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOffIcon className="w-5 h-5" />
                  ) : (
                    <EyeIcon className="w-5 h-5" />
                  )}
                </button>
              </div>
            </form>
            {(reauthLoading || deletingAfterReauth) && (
              <div className="mt-4">
                <Spinner />
              </div>
            )}
            {reauthError && (
              <div className="text-red-500 text-sm mt-2">{reauthError}</div>
            )}
          </div>
        ) : provider === "google" ? (
          <div className="flex flex-col items-center">
            {(reauthLoading || deletingAfterReauth) && (
              <div className="mt-4">
                <Spinner />
              </div>
            )}
            {reauthError && (
              <div className="text-red-500 text-sm mt-2">{reauthError}</div>
            )}
          </div>
        ) : provider === "apple" ? (
          <div className="flex flex-col items-center">
            {(reauthLoading || deletingAfterReauth) && (
              <div className="mt-4">
                <Spinner />
              </div>
            )}
            {reauthError && (
              <div className="text-red-500 text-sm mt-2">{reauthError}</div>
            )}
          </div>
        ) : null}
      </ConfirmModal>
    </>
  );
};

export default SettingsPage;
